import { useCallback } from 'react';
import { rage, useObservable } from '~/rage';
import {
	isRgbPaintType,
	IVehicleBodyColor,
	IVehiclePaint,
	ModalButtonColor,
	ModalType,
	rgbToHex,
	VehiclePaintType,
	WalletType,
} from '~/shared';
import { CategoryTypes } from '../components/CategoryTab';
import { useLayoutSelector } from '../redux';
import { useVehiclePaintLoader } from './useVehiclePaintLoader';

const useBuy = () => {
	const paintGroups = useObservable(
		rage.vehiclePaintShop.getVehiclePaintTypes(),
	);
	const rimsColorsPrice = useObservable(
		rage.vehiclePaintShop.getVehicleRimsColorPrice(),
	);
	const pearlColors = useObservable(
		rage.vehiclePaintShop.getVehiclePearlColors(),
	);
	const rimsColors = useObservable(
		rage.vehiclePaintShop.getVehicleRimsColors(),
	);
	const modalState = useObservable(rage.modal.getCurrentModal());
	const activeStage = useLayoutSelector((state) => state.carPaint.activeStage);
	const activeCategory = useLayoutSelector(
		(state) => state.carPaint.activeCategory,
	);
	const activeList = useLayoutSelector((state) => state.carPaint.activeList);
	const activeListIndex = useLayoutSelector(
		(state) => state.carPaint.activeListIndex,
	);
	const colorPickerSelectedColor = useLayoutSelector(
		(state) => state.colorPicker.selectedColor,
	);
	const sectionIndex = useLayoutSelector(
		(state) => state.colorPicker.sectionIndex,
	);
	const activePaintGroup = useLayoutSelector(
		(state) => state.colorPicker.paintGroup,
	);
	const pearlColor = useLayoutSelector((state) => state.colorPicker.pearlColor);
	const indexedColor = useLayoutSelector(
		(state) => state.colorPicker.indexedColor,
	);
	const rimsColor = useLayoutSelector((state) => state.colorPicker.rimsColor);
	const interfaceHidden = useLayoutSelector(
		(state) => state.carPaint.interfaceHidden,
	);

	const { loadVehiclePaint } = useVehiclePaintLoader();

	const handleBuy = useCallback(
		(walletType: WalletType) => {
			if (modalState || interfaceHidden) {
				return;
			}

			const vehiclePaint: Partial<IVehiclePaint> = {};

			if (
				activeStage === 'list' &&
				activeCategory === CategoryTypes.GlassTinting
			) {
				if (activeListIndex === null) {
					return;
				}

				const item = activeList[activeListIndex];
				const text = `Вы действительно хотите оплатить ${
					walletType === WalletType.Cash ? 'наличными' : 'картой'
				} товар "${item.name}"`;
				vehiclePaint.windowTint = item.value;

				rage.modal.showModal(
					{
						type: ModalType.Price,
						title: 'Подтверждение',
						text: text,
						wallet: walletType,
						price: item.price,
						acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
						cancelButton: {
							text: 'Отменить',
							color: ModalButtonColor.Secondary,
						},
					},
					(result) => {
						if (result.isAccepted) {
							rage.vehiclePaintShop.buy(vehiclePaint).then(() => {
								rage.notifications.showSuccess('Поздравляем с покупкой!');
								loadVehiclePaint();
							});
						}
					},
				);

				return;
			}

			if (activeStage === 'paint') {
				if (activeCategory === CategoryTypes.PaintingWorks) {
					if (sectionIndex === 0) {
						const currentPaintGroup = activePaintGroup || paintGroups[0];
						const color = isRgbPaintType(currentPaintGroup.type)
							? rgbToHex(colorPickerSelectedColor)
							: indexedColor;
						const bodyColor: Partial<IVehicleBodyColor> = {
							color: color === 0 ? 0 : color || '000000',
							type: currentPaintGroup.type,
						};

						if (currentPaintGroup.type === VehiclePaintType.Pearl) {
							bodyColor.pearlColor = pearlColor;
						}

						vehiclePaint.bodyColor = bodyColor as IVehicleBodyColor;

						const price = currentPaintGroup.price;
						const text = `Вы действительно хотите оплатить ${
							walletType === WalletType.Cash ? 'наличными' : 'картой'
						} покраску "${currentPaintGroup.name}"?`;

						rage.modal.showModal(
							{
								type: ModalType.Price,
								title: 'Подтверждение',
								text: text,
								wallet: walletType,
								price: price,
								acceptButton: {
									text: 'Оплатить',
									color: ModalButtonColor.Success,
								},
								cancelButton: {
									text: 'Отменить',
									color: ModalButtonColor.Secondary,
								},
							},
							(result) => {
								if (result.isAccepted) {
									rage.vehiclePaintShop.buy(vehiclePaint).then(() => {
										rage.notifications.showSuccess('Поздравляем с покупкой!');
										loadVehiclePaint();
									});
								}
							},
						);
					} else {
						vehiclePaint.interiorColor = rgbToHex(colorPickerSelectedColor);
						const price = paintGroups.find(
							(item) => item.type === VehiclePaintType.Classic,
						)?.price;
						const text = `Вы действительно хотите оплатить ${
							walletType === WalletType.Cash ? 'наличными' : 'картой'
						} покраску второго цвета?`;

						rage.modal.showModal(
							{
								type: ModalType.Price,
								title: 'Подтверждение',
								text: text,
								wallet: walletType,
								price: price || 0,
								acceptButton: {
									text: 'Оплатить',
									color: ModalButtonColor.Success,
								},
								cancelButton: {
									text: 'Отменить',
									color: ModalButtonColor.Secondary,
								},
							},
							(result) => {
								if (result.isAccepted) {
									rage.vehiclePaintShop.buy(vehiclePaint).then(() => {
										rage.notifications.showSuccess('Поздравляем с покупкой!');
										loadVehiclePaint();
									});
								}
							},
						);
					}

					return;
				}

				const selectedRimsColor = rimsColors.find(
					(item) => item.index === rimsColor,
				);
				vehiclePaint.rimsColor = selectedRimsColor?.index;
				const text = `Вы действительно хотите оплатить ${
					walletType === WalletType.Cash ? 'наличными' : 'картой'
				} покраску колёс "${selectedRimsColor?.name}"`;
				rage.modal.showModal(
					{
						type: ModalType.Price,
						title: 'Подтверждение',
						text: text,
						wallet: walletType,
						price: rimsColorsPrice,
						acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
						cancelButton: {
							text: 'Отменить',
							color: ModalButtonColor.Secondary,
						},
					},
					(result) => {
						if (result.isAccepted) {
							rage.vehiclePaintShop.buy(vehiclePaint).then(() => {
								rage.notifications.showSuccess('Поздравляем с покупкой!');
								loadVehiclePaint();
							});
						}
					},
				);
			}
		},
		[
			activeStage,
			activeCategory,
			activeListIndex,
			loadVehiclePaint,
			pearlColors,
			rimsColor,
			rimsColorsPrice,
			sectionIndex,
			activePaintGroup,
			paintGroups,
			colorPickerSelectedColor,
			indexedColor,
			pearlColor,
			rimsColors,
			modalState,
			interfaceHidden,
		],
	);

	return { handleBuy };
};

export { useBuy };
